import { IconArrowRight, IconBook2, IconMicrophone } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { allContributions } from "content-collections";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_pages/contributions")({
	component: RouteComponent,
	head: () => {
		const description = `Archive of external blog posts, podcast appearances, and industry contributions by ${config.Name}.`;
		const base = seo({
			title: `Contributions | ${config.Name}`,
			description,
			keywords:
				"contributions, podcast, articles, blog posts, interviews, email industry, software engineering",
			url: `${config.Url}/contributions`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(
					schema.collectionPage(
						`${config.Name} — Contributions`,
						`${config.Url}/contributions`,
						description,
					),
				),
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Contributions", url: `${config.Url}/contributions` },
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	const contributionsByType = Object.entries(
		allContributions.reduce(
			(acc: Record<string, typeof allContributions>, contribution) => {
				if (!acc[contribution.type]) {
					acc[contribution.type] = [];
				}
				acc[contribution.type].push(contribution);
				return acc;
			},
			{} as Record<string, typeof allContributions>,
		),
	).map(
		([type, typeContributions]) =>
			[
				type,
				typeContributions.sort(
					(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
				),
			] as [string, typeof allContributions],
	);

	return (
		<div className="relative bg-background">
			<div
				className="pointer-events-none absolute inset-0 surface-grid opacity-50"
				aria-hidden
			/>
			<main className="relative pt-12 sm:pt-20">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<div className="animate-fade-in-up">
						<MarketingHeader
							heading="Contributions"
							description="I always like to share insights into this industry. It's always fun talking about this ever changing landscape, and how it has evolved and continues to evolve."
						/>
					</div>
					<div className="pb-20">
						{contributionsByType.map(
							([type, typeContributions], sectionIndex) => (
								<section key={type} className="mb-16 last:mb-0">
									<div
										className="mb-8 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-8 duration-800 delay-200 fill-mode-both"
										style={{
											animationDelay: `${0.2 + sectionIndex * 0.2}s`,
										}}
									>
										<div
											className={cn(
												"flex size-10 items-center justify-center rounded-xl",
												type === "Blog Posts"
													? "bg-primary/10 text-primary"
													: "bg-muted text-muted-foreground",
											)}
										>
											{type === "Blog Posts" ? (
												<IconBook2 className="size-5" />
											) : (
												<IconMicrophone className="size-5" />
											)}
										</div>
										<h2 className="text-3xl font-semibold tracking-tight text-foreground">
											{type}
										</h2>
									</div>
									<div
										className={cn(
											"grid grid-cols-1 gap-6 md:grid-cols-2",
											typeContributions.length < 3
												? "lg:grid-cols-2"
												: "lg:grid-cols-3",
										)}
									>
										{typeContributions.map((contribution, index) => (
											<Link
												to={contribution.url}
												target="_blank"
												rel="noopener noreferrer"
												className="block animate-in fade-in slide-in-from-bottom-8 duration-800 delay-400 fill-mode-both"
												key={contribution.title}
												style={{
													animationDelay: `${0.4 + sectionIndex * 0.2 + index * 0.1}s`,
												}}
											>
												<Card className="group relative h-full overflow-hidden border-border/80 bg-card pt-0 shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:shadow-none dark:hover:shadow-none">
													<div className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/[0.06] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-primary/[0.04]" />
													{contribution.imgSrc && (
														<div className="relative overflow-hidden rounded-t-xl">
															<img
																src={contribution.imgSrc}
																alt={contribution.title}
																width={contribution.imgWidth}
																height={contribution.imgHeight}
																className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
															/>
															<div className="absolute inset-0 bg-linear-to-t from-card/90 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
														</div>
													)}
													<CardHeader className="relative gap-0">
														<CardTitle className="mb-3 text-xl font-semibold text-foreground transition-colors group-hover:text-primary">
															{contribution.title}
														</CardTitle>
														<CardDescription className="text-base text-pretty text-muted-foreground">
															{contribution.description}
														</CardDescription>
													</CardHeader>
													<CardFooter className="relative mt-auto pt-2">
														<div className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-colors group-hover:bg-primary/90 dark:shadow-none">
															<span>
																{contribution.type === "Blog Posts"
																	? "Read article"
																	: "Listen now"}
															</span>
															<IconArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
														</div>
													</CardFooter>
												</Card>
											</Link>
										))}
									</div>
								</section>
							),
						)}
					</div>
				</div>
			</main>
		</div>
	);
}
